import { useEffect, useState } from "react";
import api, { resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, ArrowUp, ArrowDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImageUpload from "@/components/ImageUpload";

const SCHEMAS = {
  team: {
    label: "Team",
    icon: "👥",
    fields: [
      { name: "name", label: "Name", type: "text" },
      { name: "role", label: "Role / Title", type: "text" },
      { name: "bio", label: "Biography", type: "textarea" },
      { name: "image", label: "Photo", type: "image" },
      { name: "order", label: "Display order", type: "number" },
    ],
    preview: (it) => ({ image: it.image, title: it.name, subtitle: it.role }),
  },
  testimonials: {
    label: "Testimonials",
    fields: [
      { name: "name", label: "Customer name", type: "text" },
      { name: "city", label: "Location", type: "text" },
      { name: "rating", label: "Rating (1-5)", type: "number" },
      { name: "text", label: "Testimonial", type: "textarea" },
      { name: "image", label: "Customer photo", type: "image" },
      { name: "order", label: "Display order", type: "number" },
    ],
    preview: (it) => ({ image: it.image, title: it.name, subtitle: `${it.city} · ${"★".repeat(it.rating || 5)}` }),
  },
  articles: {
    label: "Magazine articles",
    fields: [
      { name: "slug", label: "Slug", type: "text" },
      { name: "title", label: "Title (EN)", type: "text" },
      { name: "title_fr", label: "Title (FR)", type: "text" },
      { name: "excerpt", label: "Excerpt (EN)", type: "textarea" },
      { name: "excerpt_fr", label: "Excerpt (FR)", type: "textarea" },
      { name: "image", label: "Cover image", type: "image" },
      { name: "category", label: "Category", type: "text" },
      { name: "date", label: "Publication date", type: "text" },
      { name: "read_time", label: "Read time (min)", type: "number" },
      { name: "order", label: "Display order", type: "number" },
    ],
    preview: (it) => ({ image: it.image, title: it.title, subtitle: `${it.category} · ${it.date}` }),
  },
  recipes: {
    label: "Recipes",
    fields: [
      { name: "slug", label: "Slug", type: "text" },
      { name: "title", label: "Title (EN)", type: "text" },
      { name: "title_fr", label: "Title (FR)", type: "text" },
      { name: "image", label: "Image", type: "image" },
      { name: "time", label: "Time (min)", type: "number" },
      { name: "servings", label: "Servings", type: "number" },
      { name: "difficulty", label: "Difficulty", type: "text" },
      { name: "order", label: "Display order", type: "number" },
    ],
    preview: (it) => ({ image: it.image, title: it.title, subtitle: `${it.time} min · ${it.difficulty}` }),
  },
  faq: {
    label: "FAQ",
    fields: [
      { name: "q", label: "Question (EN)", type: "text" },
      { name: "q_fr", label: "Question (FR)", type: "text" },
      { name: "a", label: "Answer (EN)", type: "textarea" },
      { name: "a_fr", label: "Answer (FR)", type: "textarea" },
      { name: "order", label: "Display order", type: "number" },
    ],
    preview: (it) => ({ image: null, title: it.q, subtitle: (it.a || "").slice(0, 80) + "…" }),
  },
  awards: {
    label: "Awards",
    fields: [
      { name: "year", label: "Year", type: "text" },
      { name: "title", label: "Award title", type: "text" },
      { name: "issuer", label: "Issuer", type: "text" },
      { name: "order", label: "Display order", type: "number" },
    ],
    preview: (it) => ({ image: null, title: it.title, subtitle: `${it.year} · ${it.issuer}` }),
  },
  universes: {
    label: "Universes",
    fields: [
      { name: "slug", label: "Slug", type: "text" },
      { name: "name", label: "Name", type: "text" },
      { name: "tagline", label: "Tagline (EN)", type: "text" },
      { name: "tagline_fr", label: "Tagline (FR)", type: "text" },
      { name: "description", label: "Description (EN)", type: "textarea" },
      { name: "description_fr", label: "Description (FR)", type: "textarea" },
      { name: "image", label: "Hero image", type: "image" },
      { name: "order", label: "Display order", type: "number" },
    ],
    preview: (it) => ({ image: it.image, title: it.name, subtitle: it.tagline }),
  },
};

export default function AdminCMS() {
  const [activeTab, setActiveTab] = useState("team");

  return (
    <div className="space-y-6" data-testid="admin-cms">
      <div>
        <h1 className="font-serif text-4xl text-[var(--dsf-green)]">Site Content</h1>
        <p className="text-[var(--dsf-text-muted)] text-sm mt-1">Manage everything visitors see — team, articles, testimonials, FAQ, awards, universes.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-[var(--dsf-border)] p-1 h-auto flex-wrap justify-start" data-testid="cms-tabs">
          {Object.entries(SCHEMAS).map(([key, s]) => (
            <TabsTrigger key={key} value={key} className="text-xs uppercase tracking-wider data-[state=active]:bg-[var(--dsf-green)] data-[state=active]:text-white" data-testid={`cms-tab-${key}`}>
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(SCHEMAS).map(([key, s]) => (
          <TabsContent key={key} value={key} className="mt-6">
            <CMSSection collection={key} schema={s} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function CMSSection({ collection, schema }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const empty = () => Object.fromEntries(schema.fields.map((f) => [f.name, f.type === "number" ? 0 : ""]));

  const load = () => api.get(`/admin/cms/${collection}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [collection]);

  const onOpen = (it = null) => {
    setEditing(it);
    setForm(it ? { ...it } : empty());
    setOpen(true);
  };

  const onSave = async () => {
    try {
      const payload = { ...form };
      // ensure numeric fields are numbers
      schema.fields.forEach((f) => {
        if (f.type === "number" && payload[f.name] !== undefined && payload[f.name] !== "") {
          payload[f.name] = parseFloat(payload[f.name]) || 0;
        }
      });
      if (editing) {
        await api.patch(`/admin/cms/${collection}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/admin/cms/${collection}`, payload);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this item?")) return;
    await api.delete(`/admin/cms/${collection}/${id}`);
    toast.success("Deleted");
    load();
  };

  const move = async (item, direction) => {
    const newOrder = (item.order || 0) + direction;
    await api.patch(`/admin/cms/${collection}/${item.id}`, { order: newOrder });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[var(--dsf-text-secondary)]">{items.length} item(s)</div>
        <button onClick={() => onOpen()} className="btn-primary" data-testid={`cms-${collection}-add`}>
          <Plus size={14} /> New {schema.label.toLowerCase().replace(/s$/, "")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const p = schema.preview(it);
          return (
            <div key={it.id} className="bg-white border border-[var(--dsf-border)] p-4 hover:border-[var(--dsf-green)] transition-colors" data-testid={`cms-${collection}-item-${it.id}`}>
              {p.image && (
                <div className="aspect-[4/3] mb-3 overflow-hidden bg-[var(--dsf-sand)]">
                  <img src={resolveImage(p.image)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="text-[10px] uppercase tracking-wider text-[var(--dsf-text-muted)] mb-1">#{it.order || 0}</div>
              <div className="font-serif text-lg font-semibold text-[var(--dsf-green)] line-clamp-2">{p.title}</div>
              <div className="text-xs text-[var(--dsf-text-secondary)] mt-1 line-clamp-2">{p.subtitle}</div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--dsf-border)]">
                <div className="flex gap-1">
                  <button onClick={() => move(it, -1)} className="p-1.5 hover:bg-[var(--dsf-sand)]" data-testid={`cms-up-${it.id}`}><ArrowUp size={12} /></button>
                  <button onClick={() => move(it, 1)} className="p-1.5 hover:bg-[var(--dsf-sand)]" data-testid={`cms-down-${it.id}`}><ArrowDown size={12} /></button>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => onOpen(it)} className="p-1.5 hover:text-[var(--dsf-orange)]" data-testid={`cms-edit-${it.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => onDelete(it.id)} className="p-1.5 hover:text-[var(--dsf-burgundy)]" data-testid={`cms-delete-${it.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit" : "New"} {schema.label.toLowerCase().replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {schema.fields.map((f) => (
              <div key={f.name}>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">{f.label}</Label>
                {f.type === "textarea" ? (
                  <textarea
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    rows={3}
                    className="w-full border border-[var(--dsf-border)] px-4 py-2 mt-1 text-sm"
                    data-testid={`cms-form-${f.name}`}
                  />
                ) : f.type === "image" ? (
                  <div className="mt-2"><ImageUpload value={form[f.name] || ""} onChange={(v) => setForm({ ...form, [f.name]: v })} testid={`cms-form-${f.name}`} /></div>
                ) : (
                  <Input
                    type={f.type}
                    value={form[f.name] !== undefined ? form[f.name] : ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="mt-1"
                    data-testid={`cms-form-${f.name}`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="btn-outline-dark">Cancel</button>
            <button onClick={onSave} className="btn-primary" data-testid="cms-form-save">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
